import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'common/components';
import { SocketProvider } from 'common/context';
import { ERoute, ESearchParams, EStorageKey } from 'common/const';
import { LIST_LIMIT_0 } from 'common/config';
import { useSearchParamsHook } from 'common/hooks';
import { getStorageItem } from 'common/helpers/axios.helper';
import { CatalogCategoryPage, CatalogGoodPage, CatalogRootPage } from 'app/pages';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getCurrentCatalog } from 'entities/Catalog/Catalog.helper';
import { getCatalogList, selectCatalogList } from 'entities/Catalog/Catalog.slice';
import { CatalogEmpty } from 'entities/Catalog/components';

export const CatalogWrapper: FC = () => {
  const dispatch = useAppDispatch();
  // catalog
  const catalogList = useAppSelector(selectCatalogList);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const { catalogId, categoryId, goodId } = useParams<{ catalogId: string; categoryId: string; goodId: string }>();
  const navigate = useNavigate();
  const { getSearchParam } = useSearchParamsHook();

  const search = getSearchParam(ESearchParams.Search);
  const selectedCatalog = getCurrentCatalog(catalogList, catalogId);

  const page = useMemo(() => {
    switch (true) {
      case fetchLoading: {
        return null;
      }
      case !selectedCatalog: {
        return <CatalogEmpty />;
      }
      case !!goodId: {
        return <CatalogGoodPage catalog={selectedCatalog} />;
      }
      case !!categoryId || search !== null: {
        return <CatalogCategoryPage catalog={selectedCatalog} categoryId={Number(categoryId)} />;
      }
      default: {
        return <CatalogRootPage catalog={selectedCatalog} />;
      }
    }
  }, [fetchLoading, selectedCatalog, categoryId, goodId, search]);

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);
      const userCatalogId = getStorageItem<number>(EStorageKey.UserCatalogId);
      const response = await dispatch(getCatalogList({ limit: LIST_LIMIT_0 }));
      const defaultCatalogId = response?.[0]?.id;

      if (!catalogId) {
        if (userCatalogId) {
          navigate(`${ERoute.Catalog}/${userCatalogId}`, { replace: true });
        } else if (defaultCatalogId) {
          navigate(`${ERoute.Catalog}/${defaultCatalogId}`, { replace: true });
        }
      }
      setFetchLoading(false);
    };

    fetch();
  }, []);

  if (fetchLoading) {
    return (
      <div className="h-full flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  return <SocketProvider>{page}</SocketProvider>;
};
